import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../App.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { sellFlight } from "./FareReviewPayloadRequest";

/* =========================================================
   BRANDING (yahan se badlo)
   Bookbyown:   { name: "Bookbyown",   logo: "/BBOLogo.png" }
   TripCaliber: { name: "TripCaliber", logo: "/tripcaliber-logo.png" }
========================================================= */
const BRAND = { name: "Bookbyown", logo: "/BBOLogo.png" };

/* Agar state se agencyKey khaali aaye to ye fallback use hoga */
const DEFAULT_AGENCY_KEY = "02AD68F3-9AE6-4148-85B1-E61D1F03991F";

/* =========================================================
   HELPERS
========================================================= */
const getNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const getGrossFare = (f) =>
  getNumber(f?.GrossFare ?? f?.TotalFare ?? f?.PublishedFare ?? f?.OfferedFare ?? 0);
const getBaseFare = (f) => getNumber(f?.BaseFare ?? f?.BasicFare ?? f?.Fare ?? 0);
const getTax = (f) => getNumber(f?.TaxFare ?? f?.Tax ?? f?.Taxes ?? 0);
const getDiscount = (f) => getNumber(f?.Discount ?? 0);
const getAddons = (f) => getNumber(f?.AdditionalCharges ?? 0);

// ContractId 0 bhi valid ho sakta hai → ?? use kiya (|| nahi)
const getContractId = (flight) => {
  if (!flight) return null;
  const id =
    flight?.ContractId ??
    flight?.AirlineFare?.ContractId ??
    flight?.AirlineFares?.[0]?.ContractId ??
    flight?.Id ??
    null;
  // empty string / undefined guard
  if (id === null || id === undefined || id === "") return null;
  return id;
};

const isMissingId = (id) => id === null || id === undefined || id === "";

const formatPrice = (amount) => <>₹{Number(amount || 0).toLocaleString("en-IN")}</>;

const safeDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? null : date;
};
const formatFullDate = (d) => {
  const date = safeDate(d);
  if (!date) return d || "N/A";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
};
const formatShortDate = (d) => {
  const date = safeDate(d);
  if (!date) return d || "N/A";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }).toUpperCase();
};
const formatTime = (d) => {
  const date = safeDate(d);
  if (!date) return d || "N/A";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
};

const segTypeOf = (seg) =>
  String(seg?.SegmentType || seg?.TripType || seg?.JourneyType || seg?.Direction || "").toLowerCase();
const isOnwardType = (t) => t === "onword" || t === "onward" || t === "outbound";
const isReturnType = (t) => t === "return" || t === "inbound";

const findFareByType = (fares, wanted) =>
  (fares || []).find((f) => {
    const t = String(
      f?.ContractType || f?.SegmentType || f?.JourneyType || f?.Direction || ""
    ).toLowerCase();
    return wanted === "return" ? isReturnType(t) : isOnwardType(t);
  });

/* =========================================================
   AIRPLANE LOADER
========================================================= */
const AirplaneLoader = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 99999,
      background: "rgba(0,0,0,0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <div
      style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)",
        borderRadius: 20,
        padding: "40px 48px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 28,
        boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
        minWidth: 380,
      }}
    >
      <img
        src={BRAND.logo}
        alt={BRAND.name}
        style={{ width: 160, filter: "brightness(0) invert(1)", opacity: 0.9 }}
      />

      <div style={{ position: "relative", width: 300, height: 80 }}>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            animation: "fadeCloud 1.5s ease-in-out infinite alternate",
          }}
        >
          <span style={{ fontSize: 22, opacity: 0.4 }}>☁️</span>
          <span style={{ fontSize: 16, opacity: 0.3 }}>☁️</span>
          <span style={{ fontSize: 24, opacity: 0.4 }}>☁️</span>
        </div>
        <div
          style={{
            position: "absolute",
            top: 18,
            fontSize: 36,
            animation: "flyPlane 2s ease-in-out infinite alternate",
            filter: "drop-shadow(0 4px 12px rgba(99,179,237,0.5))",
          }}
        >
          ✈️
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 4,
            background: "linear-gradient(90deg, transparent, #60a5fa, #3b82f6, #60a5fa, transparent)",
            borderRadius: 2,
            animation: "glowLine 1.5s ease-in-out infinite alternate",
          }}
        />
      </div>

      <div style={{ textAlign: "center" }}>
        <p
          style={{
            color: "#fff",
            fontSize: 18,
            fontWeight: 600,
            margin: 0,
            animation: "fadeText 1.5s ease-in-out infinite alternate",
          }}
        >
          We are going to next section...
        </p>
        <p style={{ color: "#93c5fd", fontSize: 13, margin: "8px 0 0", opacity: 0.8 }}>
          Please wait while we prepare your booking
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14 }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#3b82f6",
                animation: `dotBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes flyPlane { 0% { left: 0; transform: translateY(0) rotate(5deg); } 100% { left: 255px; transform: translateY(-12px) rotate(-3deg); } }
        @keyframes glowLine { 0% { opacity: .5; } 100% { opacity: 1; box-shadow: 0 0 12px #3b82f6; } }
        @keyframes fadeText { 0% { opacity: .7; } 100% { opacity: 1; } }
        @keyframes fadeCloud { 0% { opacity: .3; transform: translateX(0); } 100% { opacity: .6; transform: translateX(8px); } }
        @keyframes dotBounce { 0%, 100% { transform: translateY(0); opacity: .4; } 50% { transform: translateY(-8px); opacity: 1; } }
      `}</style>
    </div>
  </div>
);

/* =========================================================
   FARE RULES MODAL
========================================================= */
function FareRulesModal({ fare, flight, onClose }) {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const rawRules =
    fare?.FareRules ||
    fare?.FareRuleText ||
    fare?.MiniRules ||
    flight?.FareRules ||
    flight?.FareGroups?.FareGroup?.[0]?.DateChange ||
    null;
  const rulesText = Array.isArray(rawRules) ? rawRules.join("\n\n") : rawRules;
  const cancellationFee = fare?.CancellationFee ?? fare?.PenaltyAmount ?? null;
  const changeFee = fare?.ChangeFee ?? fare?.DateChangeFee ?? null;

  const pill = {
    background: "#F0F1F5",
    color: "#374151",
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 12,
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99998,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          width: "100%",
          maxWidth: 560,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 25px 60px rgba(0,0,0,0.35)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #E8EAF0",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 700, color: "#14213D" }}>
            <i className="bi bi-info-circle" style={{ marginRight: 8 }} />
            Fare Rules
          </span>
          <button
            type="button"
            aria-label="Close fare rules"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              lineHeight: 1,
              cursor: "pointer",
              color: "#6B7280",
              padding: 4,
            }}
          >
            ✕
          </button>
        </div>

        <div
          style={{
            padding: "18px 20px",
            overflowY: "auto",
            fontSize: 13.5,
            color: "#374151",
            lineHeight: 1.6,
          }}
        >
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <span
              style={{
                background: fare?.Refundable ? "#E6F4EA" : "#FFF3E0",
                color: fare?.Refundable ? "#27600A" : "#854F0B",
                padding: "4px 10px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {fare?.Refundable ? "Refundable" : "Either  Refundable Or Non Refundable"}
            </span>
            {cancellationFee != null && (
              <span style={pill}>
                Cancellation fee: ₹{Number(cancellationFee).toLocaleString("en-IN")}
              </span>
            )}
            {changeFee != null && (
              <span style={pill}>
                Date change fee: ₹{Number(changeFee).toLocaleString("en-IN")}
              </span>
            )}
          </div>

          {rulesText ? (
            <div style={{ whiteSpace: "pre-line" }}>{rulesText}</div>
          ) : (
            <div style={{ color: "#9295A4" }}>
              Detailed fare rules aren't available from the airline for this fare at the moment.
              Standard airline cancellation and date-change penalties apply — please confirm exact
              charges with the airline or during checkout before booking.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   FLIGHT CARD
========================================================= */
const FlightCard = ({ segment, journeyType, totalSegments }) => {
  const isReturn = journeyType === "Return";
  const origin = segment?.Origen || segment?.Origin || "N/A";

  return (
    <div className="view_box">
      <div className="flight_preview">
        <div className="flight_preview_head">
          <div className="left">
            <span className="city">
              <span style={{ fontWeight: 700, color: isReturn ? "#dc2626" : "#2563eb" }}>
                {journeyType}
              </span>
              &nbsp;&nbsp;
              <b>
                {origin}
                <i className="bi bi-arrow-right" style={{ margin: "0 8px" }} />
                {segment?.Destination || "N/A"}
              </b>
            </span>
            <span className="jurney">
              {formatShortDate(segment?.DepartureDateTime)} |{" "}
              {totalSegments <= 1 ? "0 Stop" : `${totalSegments - 1} Stop`} |{" "}
              {segment?.Duration || "N/A"}
            </span>
          </div>
        </div>

        <div className="flight_preview_details">
          <div className="flight_col1">
            <div className="f_img">
              <img
                src={`/Flight_Images/${segment?.AirlineCode || ""}.gif`}
                alt={segment?.AirlineName || "Airline"}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
            <div>
              <span className="flight_name">{segment?.AirlineName || "Airline"}</span>
              <span className="flight_num">
                {segment?.AirlineCode || ""}-{segment?.FlightNumber || ""}
              </span>
            </div>
          </div>

          <div className="flight_col2">
            <span className="t1">Travel Class</span>
            <span className="t2">{segment?.CabinClass || "Economy"}</span>
          </div>
          <div className="flight_col2">
            <span className="t1">Check-In Baggage</span>
            <span className="t2">
              Adult - {segment?.BaggageAllowed?.CheckInBaggage || "15 Kg"}
            </span>
          </div>
          <div className="flight_col2">
            <span className="t1">Cabin Baggage</span>
            <span className="t2">
              Adult - {segment?.BaggageAllowed?.HandBaggage || "7 Kg"}
            </span>
          </div>
        </div>

        <div className="flight_preview_content">
          <div className="from">
            <span className="time">{formatTime(segment?.DepartureDateTime)}</span>
            <span className="date">{formatFullDate(segment?.DepartureDateTime)}</span>
            <span className="l1">
              {segment?.SourceCity || segment?.OriginCity || "N/A"} <b>[{origin}]</b>
            </span>
            {segment?.DepartureTerminal && (
              <span className="l1">Terminal {segment.DepartureTerminal}</span>
            )}
          </div>

          <div className="duration">
            <span className="t_time">{segment?.Duration || "N/A"}</span>
            <span className="t_arrow">
              <i className="fa fa-plane"></i>
            </span>
          </div>

          <div className="to">
            <span className="time">{formatTime(segment?.ArrivalDateTime)}</span>
            <span className="date">{formatFullDate(segment?.ArrivalDateTime)}</span>
            <span className="l1">
              {segment?.DestinationCity || "N/A"} <b>[{segment?.Destination || "N/A"}]</b>
            </span>
            {(segment?.DestinationAirportTerminal || segment?.Terminal) && (
              <span className="l1">
                {segment.DestinationAirportTerminal || `Terminal ${segment.Terminal}`}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   FARE CARD
========================================================= */

const FareCard = ({ fareData, onShowRules, title = "VALUE FARE", subtitle = "Standard fare benefits", priceLabel = "Total fare" }) => (
  <div className="fare_wrap">
    <div className="fare_box">
      {/* Header */}
      <div className="fare_header">
        <div className="fare_heading">
          <span className="fare_icon">
            <i className="bi bi-airplane"></i>
          </span>

          <div>
            <h3 className="fare_title">{title}</h3>
            <span className="fare_subtitle">
              {subtitle}
            </span>
          </div>
        </div>

        <span className="fare_tag">STANDARD</span>
      </div>

      {/* Price */}
      <div className="fare_price_section">
        <span className="fare_price_label">{priceLabel}</span>

        <div className="fare">
          {formatPrice(getGrossFare(fareData))}
        </div>

        <p className="fare_desc">
          Standard airline cancellation and date change
          penalties apply.
        </p>
      </div>

      {/* Footer */}
      <div className="fare_footer">
        <span
          className={`fare_refund_badge ${
            fareData?.Refundable
              ? "is-refundable"
              : "is-non-refundable"
          }`}
        >
          <i
            className={`bi ${
              fareData?.Refundable
                ? "bi-check-circle-fill"
                : "bi-info-circle-fill"
            }`}
          ></i>

          {fareData?.Refundable
            ? "Refundable"
            : "Either Refundable Or Non Refundable"}
        </span>

        <button
          type="button"
          className="btn_farerules"
          onClick={() => onShowRules(fareData)}
        >
          <i className="bi bi-info-circle"></i>
          Fare Rules
          <i className="bi bi-chevron-right fare_arrow"></i>
        </button>
      </div>
    </div>
  </div>
);


/* =========================================================
   MAIN
========================================================= */
export default function FareDetails() {
  const [loading, setLoading] = useState(false);
  const [showFareRules, setShowFareRules] = useState(false);
  const [selectedFare, setSelectedFare] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const flight = state.flight;
  const returnFlightState =
    state.returnFlight || state.selectedReturnFlight || state.returnContract || null;
  const isSplitPair =
    (!!returnFlightState && returnFlightState !== flight) || !!state.selectedReturnFare;

  const apiToken = state.apiToken;
  const agencyKey = state.agencyKey;
  const bookingKey = state.bookingKey;
  const passengers = state.passengers || [];
  const returnApiToken = state.returnApiToken || apiToken;
  const returnBookingKey = state.returnBookingKey || bookingKey;

  const adultCount = state.AdultCount || flight?.AdultCount || 1;
  const childCount = state.ChildCount || flight?.ChildCount || 0;
  const infantCount = state.InfantCount || flight?.InfantCount || 0;

  // Debug on mount / state change
  console.log("========== FareDetails STATE ==========");
  console.log("isSplitPair:", isSplitPair);
  console.log("state keys:", Object.keys(state));
  console.log("flight.ContractId:", flight?.ContractId);
  console.log("flight.AirlineFare?.ContractId:", flight?.AirlineFare?.ContractId);
  console.log("return ContractId:", getContractId(returnFlightState));
  console.log("ONWARD ->", getContractId(flight), "| token:", apiToken, "| bk:", bookingKey);
  console.log(
    "RETURN ->",
    getContractId(returnFlightState),
    "| token:",
    returnApiToken,
    "| bk:",
    returnBookingKey
  );
  console.log("allContracts:", state.allContracts);
  console.log("=======================================");

  /* ---------------- SEGMENTS ---------------- */
  const allSegments = flight?.AirSegments || flight?.Segments || flight?.FlightSegments || [];

  let onwardSegments = allSegments.filter((s) => isOnwardType(segTypeOf(s)));
  let returnSegments = allSegments.filter((s) => isReturnType(segTypeOf(s)));

  if (onwardSegments.length === 0 && returnSegments.length === 0) {
    onwardSegments = allSegments;
  }

  if (isSplitPair) {
    const rs =
      returnFlightState?.AirSegments ||
      returnFlightState?.Segments ||
      returnFlightState?.FlightSegments ||
      [];
    const typed = rs.filter((s) => isReturnType(segTypeOf(s)));
    returnSegments = typed.length > 0 ? typed : rs;
  }

  const hasReturn = returnSegments.length > 0;

  /* ---------------- FARES ---------------- */
  const airlineFares = Array.isArray(flight?.AirlineFares) ? flight.AirlineFares : [];

  const onwardFare =
    flight?.AirlineFare || findFareByType(airlineFares, "onward") || airlineFares[0] || {};

  const returnFare = isSplitPair
    ? returnFlightState?.AirlineFare ||
      findFareByType(returnFlightState?.AirlineFares, "return") ||
      returnFlightState?.AirlineFares?.[0] ||
      {}
    : findFareByType(airlineFares, "return") ||
      (airlineFares.length > 1 ? airlineFares[1] : null) ||
      {};

  // Fallback: contract me fare na mile to FareSelect modal me chuna hua fare use karo
  const onwardFareFinal =
    getGrossFare(onwardFare) > 0 ? onwardFare : state.selectedFare || onwardFare;
  const returnFareFinal =
    getGrossFare(returnFare) > 0 ? returnFare : state.selectedReturnFare || returnFare;

  const onwardGross = getGrossFare(onwardFareFinal);
  const returnGross = getGrossFare(returnFareFinal);
  const showReturnFare = hasReturn && returnGross > 0;
  // International combined round trip: ek hi contract/fare hota hai jo poore
  // round trip (onward + return) ka hota hai, alag return fare nahi aata.
  const combinedRT = hasReturn && !showReturnFare;
  console.log("FARE DEBUG ->", { isSplitPair, hasReturn, onwardGross, returnGross, combinedRT, airlineFares, onwardFare, returnFare, sameObject: returnFlightState === flight });

  const totalFare = onwardGross + (showReturnFare ? returnGross : 0);
  const totalBase =
    getBaseFare(onwardFareFinal) + (showReturnFare ? getBaseFare(returnFareFinal) : 0);
  const totalTax = getTax(onwardFareFinal) + (showReturnFare ? getTax(returnFareFinal) : 0);
  const totalDiscount =
    getDiscount(onwardFareFinal) + (showReturnFare ? getDiscount(returnFareFinal) : 0);
  const totalAddons =
    getAddons(onwardFareFinal) + (showReturnFare ? getAddons(returnFareFinal) : 0);
  const infantTotal =
    getNumber(onwardFareFinal?.InfantBaseFare) +
    (showReturnFare ? getNumber(returnFareFinal?.InfantBaseFare) : 0);

  const openRules = (f) => {
    setSelectedFare(f);
    setShowFareRules(true);
  };

 
 const handleContinue = async () => {
  setLoading(true);
  try {
   // Chuni hui fare ki ID (FareGroup) ko priority, warna contract ki apni ID
const onwardId = state.selectedFare?.ContractId ?? getContractId(flight);
const returnId = isSplitPair
  ? (state.selectedReturnFare?.ContractId ?? getContractId(returnFlightState))
  : null;

console.log("Sell IDs -> onward:", onwardId, "| return:", returnId, "| split:", isSplitPair);

if (isMissingId(onwardId) || (isSplitPair && isMissingId(returnId))) {
  alert("Contract ID missing — cannot proceed with booking.");
  return;
}

// ID ya to contract ki ho sakti hai, ya uske FareGroup ki
const findContract = (id) => {
  const all = state.allContracts || [];
  return (
    all.find((c) => String(c?.ContractId) === String(id)) ||
    all.find((c) =>
      (c?.FareGroups?.FareGroup || []).some((g) => String(g?.ContractId) === String(id))
    )
  );
};

const matchedOnward = findContract(onwardId);
const matchedReturn = isSplitPair ? findContract(returnId) : null;

if (!matchedOnward) {
  alert(`Onward ContractId ${onwardId} allContracts me nahi mila`);
  return;
}
if (isSplitPair && !matchedReturn) {
  alert(`Return ContractId ${returnId} allContracts me nahi mila`);
  return;
}

    // Prefer contract se aaya BookingKey / Token (state se mismatch ho to)
    const sellBookingKey = matchedOnward._bookingKey || bookingKey;
    const sellApiToken = matchedOnward._apiToken || apiToken;
    const effectiveAgencyKey = agencyKey?.trim() ? agencyKey : DEFAULT_AGENCY_KEY;

    const contractIdToSell = isSplitPair
      ? `${onwardId}^${returnId}`
      : String(onwardId);

    const sellPayloadFlight = {
      ...flight,
      ContractId: contractIdToSell,
    };

    console.log("SELL body will be:", {
      APIToken: sellApiToken,
      AgencyKey: effectiveAgencyKey,
      BookingKey: sellBookingKey,
      ContractId: contractIdToSell,
      IsB2B: false,
    });

    const data = await sellFlight(
      sellPayloadFlight,
      sellApiToken,
      effectiveAgencyKey,
      sellBookingKey
    );

    console.log("SELL response:", data);

    if (!data || data.ResponseStatus !== 1) {
      alert("Sell failed: " + (data?.Error?.ErrorDesc || "Unknown error"));
      return;
    }

    const freshBookingKey =
      data?.BookingKey || data?.Sell?.BookingKey || sellBookingKey;

    navigate("/FlightReview", {
      state: {
        sellResponse: data,
        returnSellResponse: null,
        returnFlight: isSplitPair ? returnFlightState : undefined,
        flight: {
          ...flight,
          IsInternational: !flight?.IsDomestic,
          AdultCount: adultCount,
          ChildCount: childCount,
          InfantCount: infantCount,
          BookingKey: freshBookingKey,
          OnwardSegments: onwardSegments,
          ReturnSegments: returnSegments,
          OnwardFare: onwardFareFinal,
          ReturnFare: returnFareFinal,
          TotalFare: totalFare,
        },
        apiToken: sellApiToken,
        agencyKey: effectiveAgencyKey,
        bookingKey: freshBookingKey,
      },
    });
  } catch (err) {
    console.error("Sell Error:", err);
    alert("Something went wrong while fetching fare details.");
  } finally {
    setLoading(false);
  }
};
  if (!flight) {
    return (
      <div style={{ padding: 50, textAlign: "center" }}>No Flight Data Found</div>
    );
  }

  return (
    <div className="fare-details-page">
      {loading && <AirplaneLoader />}

      {showFareRules && (
        <FareRulesModal
          fare={selectedFare || onwardFareFinal}
          flight={flight}
          onClose={() => setShowFareRules(false)}
        />
      )}

      <div style={{ display: "flex", gap: 25, flexWrap: "wrap" }}>
        {/* ──────────── LEFT ──────────── */}
        <div style={{ flex: 2, minWidth: 650 }}>
          {onwardSegments.length > 0 && (
            <>
              <div className="row">
                <div className="col-sm-6">
                  <span className="section_title">✈ Onward</span>
                </div>
              </div>
              {onwardSegments.map((seg, idx) => (
                <FlightCard
                  key={`onward-${idx}`}
                  segment={seg}
                  journeyType="Onward"
                  totalSegments={onwardSegments.length}
                />
              ))}
          
            </>
          )}

          {hasReturn && (
            <>
            
              <div className="row" style={{ marginTop: 25 }}>
                <div className="col-sm-6">
                  <span className="section_title" style={{ color: "#c2185b" }}>
                    ↩ Return
                  </span>
                </div>
              </div>
              {returnSegments.map((seg, idx) => (
                <FlightCard
                  key={`return-${idx}`}
                  segment={seg}
                  journeyType="Return"
                  totalSegments={returnSegments.length}
                />
              ))}
           
            </>
          )}
{/* VALUE FARE SECTION - AFTER ONWARD & RETURN */}

<div className="value-fare-section">
  <div className="row">
    <div className="col-12">
      <span className="section_title">Select Your Fare</span>
    </div>
  </div>

  <div className="value-fare-grid">
    <div className="value-fare-item">
      <h4>{combinedRT ? "Round Trip Fare (Onward + Return)" : "Onward Fare"}</h4>
      <FareCard
        fareData={onwardFareFinal}
        onShowRules={openRules}
        {...(combinedRT
          ? { title: "ROUND TRIP FARE", subtitle: "Onward + Return combined", priceLabel: "Total fare (Onward + Return)" }
          : {})}
      />
    </div>

    {showReturnFare && (
      <div className="value-fare-item">
        <h4>Return Fare</h4>
        <FareCard
          fareData={returnFareFinal}
          onShowRules={openRules}
        />
      </div>
    )}
  </div>
</div>
          <div className="view_box" style={{ marginTop: 16 }}>
            <div className="flight_preview">
              <div className="flight_preview_head">
                <div className="left">
                  <span className="title">🔒 Log-in to your {BRAND.name} Account</span>
                </div>
                <div className="right">
                  <button className="link_login" type="button">
                    Log In
                  </button>
                  <button
                    className="btn_loginguest"
                    type="button"
                    onClick={handleContinue}
                    disabled={loading}
                  >
                    Continue as Guest
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ──────────── RIGHT: FARE SUMMARY ──────────── */}
        <div style={{ flex: 1, minWidth: 320 }}>
          <div className="right_section">
            <span className="section_title">Fare Summary</span>

            {passengers.length > 0 && (
              <div className="traveller_section">
                <span className="sub_title">Traveller Details</span>
                {passengers.map((pax, index) => (
                  <div className="traveller_card" key={index}>
                    <div className="traveller_row">
                      <span className="traveller_name">
                        {pax.Title} {pax.FirstName} {pax.LastName}
                      </span>
                      <span
                        className={`pax_badge ${
                          pax.PaxType === 1
                            ? "adult"
                            : pax.PaxType === 2
                            ? "child"
                            : "infant"
                        }`}
                      >
                        {pax.PaxType === 1
                          ? "Adult"
                          : pax.PaxType === 2
                          ? "Child"
                          : "Infant"}
                      </span>
                    </div>
                    {pax.DateOfBirth && (
                      <div className="traveller_info">
                        <span>DOB:</span> <span>{pax.DateOfBirth}</span>
                      </div>
                    )}
                    {pax.PassportNo && (
                      <div className="traveller_info">
                        <span>Passport:</span> <span>{pax.PassportNo}</span>
                      </div>
                    )}
                    {pax.ContactNo && (
                      <div className="traveller_info">
                        <span>Contact:</span> <span>{pax.ContactNo}</span>
                      </div>
                    )}
                    {pax.Email && (
                      <div className="traveller_info">
                        <span>Email:</span> <span>{pax.Email}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {combinedRT && (
              <div className="price_panel" style={{ background: "#f8fafc" }}>
                <span className="titel">Round Trip Fare (Onward + Return)</span>
                <span className="price">{formatPrice(onwardGross)}</span>
              </div>
            )}

            {showReturnFare && (
              <>
                <div className="price_panel" style={{ background: "#f8fafc" }}>
                  <span className="titel">Onward Fare</span>
                  <span className="price">{formatPrice(onwardGross)}</span>
                </div>
                <div className="price_panel" style={{ background: "#f8fafc" }}>
                  <span className="titel">Return Fare</span>
                  <span className="price">{formatPrice(returnGross)}</span>
                </div>
              </>
            )}

            <div className="price_panel">
              <span className="titel">
                Adult × {adultCount}
                {childCount > 0 && ` | Child × ${childCount}`} (BaseFare)
              </span>
              <span className="price">{formatPrice(totalBase)}</span>
            </div>

            {infantCount > 0 && (
              <div className="price_panel">
                <span className="titel">Infant × {infantCount}</span>
                <span className="price">{formatPrice(infantTotal)}</span>
              </div>
            )}

            <div className="price_panel">
              <span className="titel">Tax</span>
              <span className="price">{formatPrice(totalTax)}</span>
            </div>

            <div className="price_panel">
              <span className="titel">Discount</span>
              <span className="price">- {formatPrice(totalDiscount)}</span>
            </div>

            <div className="f_total">
              <span>Total Fare</span>
              <span>{formatPrice(totalFare)}</span>
            </div>

            <div className="price_panel">
              <span className="titel">Add-Ons</span>
              <span className="price">{formatPrice(totalAddons)}</span>
            </div>

            <div className="pay_total">
              <span>You Pay :</span>
              <span>{formatPrice(totalFare + totalAddons - totalDiscount)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}